import type { JSX } from 'react';
import { useState } from 'react';
import { api } from '../client';
import { FormFooter } from './form-footer';
import { CodeWarning, StepBars, ctaLabel, loginMessage } from './telegram-login-parts';
import type { AccountForm, SecretForm } from './telegram-login-schema';
import { AccountStep, SecretStep } from './telegram-login-steps';

type Step = 1 | 2 | 3;

/**
 * Connecting a Telegram account in the three steps Telegram uses: phone, the
 * code it sends, and a two-factor password only when the account has one. The
 * finished login is stored as a source by the API; `onDone` closes the modal.
 */
export function TelegramLogin({
  onDone,
  back,
}: {
  onDone: () => void;
  back: { label: string; run: () => void };
}): JSX.Element {
  const [step, setStep] = useState<Step>(1);
  const [phone, setPhone] = useState('');
  const [loginId, setLoginId] = useState('');
  const [refused, setRefused] = useState<string | undefined>(undefined);

  const start = async (account: AccountForm): Promise<void> => {
    const r = await api.telegramLogin.start({ body: account });
    if (r.status !== 201) return setRefused(loginMessage(r.status, r.body));
    setRefused(undefined);
    setPhone(account.phone);
    setLoginId(r.body.loginId);
    setStep(2);
  };

  const answer = async ({ secret }: SecretForm): Promise<void> => {
    const r =
      step === 2
        ? await api.telegramLogin.code({ params: { loginId }, body: { code: secret.trim() } })
        : await api.telegramLogin.password({ params: { loginId }, body: { password: secret } });
    if (r.status !== 200) return setRefused(loginMessage(r.status, r.body));
    setRefused(undefined);
    if (r.body.status === 'password-required') setStep(3);
    if (r.body.status === 'completed') onDone();
    if (r.body.status === 'expired') setRefused(loginMessage(410, r.body));
  };

  const footer = (busy: boolean): JSX.Element => (
    <FormFooter back={back} busy={busy} label={ctaLabel(step, busy)} />
  );

  return (
    <div className="flex flex-1 flex-col gap-3.5">
      <StepBars reached={step} />
      <CodeWarning />
      {step === 1 ? (
        <AccountStep onSubmit={start} refused={refused} footer={footer} />
      ) : (
        <SecretStep
          key={step}
          step={step}
          phone={phone}
          onSubmit={answer}
          refused={refused}
          footer={footer}
        />
      )}
    </div>
  );
}
